import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  MessageSquare, Bug, HelpCircle, Flag, Clock, CheckCircle, 
  AlertCircle, XCircle, ChevronRight, User, Image, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  feedback: { label: 'Feedback', icon: MessageSquare, color: 'text-blue-400' },
  support: { label: 'Support', icon: HelpCircle, color: 'text-yellow-400' },
  bug_report: { label: 'Bug Report', icon: Bug, color: 'text-orange-400' },
  user_report: { label: 'User Report', icon: Flag, color: 'text-red-400' }
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  in_progress: { label: 'In Progress', icon: AlertCircle, color: 'text-blue-400', bg: 'bg-blue-400/10' },
  resolved: { label: 'Resolved', icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-400/10' },
  closed: { label: 'Closed', icon: XCircle, color: 'text-gray-400', bg: 'bg-gray-400/10' }
};

const AdminContact = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchSubmissions();
  }, [filterType, filterStatus]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const response = await axios.get(`${API}/admin/contact?${params.toString()}`);
      setSubmissions(response.data.submissions);
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (submission) => {
    setDetailLoading(true);
    setShowDetailModal(true);
    try {
      const response = await axios.get(`${API}/admin/contact/${submission.id}`);
      setSelectedSubmission(response.data);
      setAdminNotes(response.data.admin_notes || '');
    } catch (error) {
      toast.error('Failed to load submission details');
      setShowDetailModal(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    if (!selectedSubmission) return;
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/admin/contact/${selectedSubmission.id}/status`, {
        status: newStatus,
        admin_notes: adminNotes || null
      });
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
      setSelectedSubmission(prev => ({ ...prev, status: newStatus, admin_notes: adminNotes }));
      fetchSubmissions();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getPhotoUrl = (photos) => {
    if (!photos || photos.length === 0) return null;
    const photo = photos[0];
    if (photo.startsWith('data:') || photo.startsWith('http')) return photo;
    return `data:image/jpeg;base64,${photo}`;
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#EDEDED]">Contact Submissions</h1>
            <p className="text-[#A1A1AA] text-sm mt-1">Manage feedback, support requests, and reports</p>
          </div>
          <Button
            onClick={fetchSubmissions}
            variant="outline"
            className="border-white/20 text-[#EDEDED] hover:bg-white/10"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <div className="glass rounded-xl p-4">
            <p className="text-[#A1A1AA] text-sm">Total</p>
            <p className="text-2xl font-bold text-[#EDEDED]">{stats.total || 0}</p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-yellow-400 text-sm">Pending</p>
            <p className="text-2xl font-bold text-yellow-400">{stats.pending || 0}</p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-blue-400 text-sm">In Progress</p>
            <p className="text-2xl font-bold text-blue-400">{stats.in_progress || 0}</p>
          </div>
          <div className="glass rounded-xl p-4">
            <p className="text-green-400 text-sm">Resolved</p>
            <p className="text-2xl font-bold text-green-400">{stats.resolved || 0}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="feedback">Feedback</SelectItem>
              <SelectItem value="support">Support</SelectItem>
              <SelectItem value="bug_report">Bug Report</SelectItem>
              <SelectItem value="user_report">User Report</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Submissions List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="glass rounded-xl p-12 text-center">
            <MessageSquare className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">No submissions found</p>
          </div>
        ) : (
          <div className="glass rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Type</th>
                  <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Subject</th>
                  <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">From</th>
                  <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Status</th>
                  <th className="text-left p-4 text-[#A1A1AA] text-sm font-medium">Date</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((submission) => {
                  const typeConfig = TYPE_CONFIG[submission.type] || TYPE_CONFIG.feedback;
                  const statusConfig = STATUS_CONFIG[submission.status] || STATUS_CONFIG.pending;
                  const TypeIcon = typeConfig.icon;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <tr 
                      key={submission.id}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                      onClick={() => openDetail(submission)}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <TypeIcon className={`w-4 h-4 ${typeConfig.color}`} />
                          <span className="text-[#EDEDED] text-sm">{typeConfig.label}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-[#EDEDED] text-sm truncate max-w-xs">{submission.subject}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-[#A1A1AA] text-sm">{submission.user_name || submission.user_email}</p>
                      </td>
                      <td className="p-4">
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${statusConfig.bg}`}>
                          <StatusIcon className={`w-3 h-3 ${statusConfig.color}`} />
                          <span className={`text-xs ${statusConfig.color}`}>{statusConfig.label}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-[#52525B] text-sm">{formatDate(submission.created_at)}</p>
                      </td>
                      <td className="p-4">
                        <ChevronRight className="w-4 h-4 text-[#52525B]" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          {detailLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : selectedSubmission && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {(() => {
                    const typeConfig = TYPE_CONFIG[selectedSubmission.type] || TYPE_CONFIG.feedback;
                    const TypeIcon = typeConfig.icon;
                    return <TypeIcon className={`w-5 h-5 ${typeConfig.color}`} />;
                  })()}
                  <span>{selectedSubmission.subject}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* User Info */}
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
                  <div className="w-12 h-12 rounded-full bg-[#FF2E63]/20 flex items-center justify-center">
                    {selectedSubmission.user_details?.public_photos?.[0] ? (
                      <img 
                        src={getPhotoUrl(selectedSubmission.user_details.public_photos)}
                        alt=""
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <User className="w-6 h-6 text-[#FF2E63]" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-[#EDEDED]">
                      {selectedSubmission.user_details?.display_name || selectedSubmission.user_name}
                    </p>
                    <p className="text-sm text-[#A1A1AA]">{selectedSubmission.user_email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/admin/users/${selectedSubmission.user_id}`)}
                    className="ml-auto text-[#08D9D6] hover:bg-[#08D9D6]/10"
                  >
                    View Profile
                  </Button>
                </div>

                {/* Reported User (if applicable) */}
                {selectedSubmission.reported_user_details && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-sm text-red-400 mb-2">Reported User:</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                        {selectedSubmission.reported_user_details?.public_photos?.[0] ? (
                          <img 
                            src={getPhotoUrl(selectedSubmission.reported_user_details.public_photos)}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-5 h-5 text-red-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-[#EDEDED]">
                          {selectedSubmission.reported_user_details?.display_name}
                        </p>
                        <p className="text-sm text-[#A1A1AA]">
                          {selectedSubmission.reported_user_details?.email}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/admin/users/${selectedSubmission.reported_user_id}`)}
                        className="ml-auto text-red-400 hover:bg-red-500/10"
                      >
                        View Profile
                      </Button>
                    </div>
                  </div>
                )}

                {/* Message */}
                <div>
                  <p className="text-sm text-[#A1A1AA] mb-2">Message:</p>
                  <div className="p-4 bg-white/5 rounded-lg">
                    <p className="text-[#EDEDED] whitespace-pre-wrap">{selectedSubmission.message}</p>
                  </div>
                </div>

                {/* Screenshot */}
                {selectedSubmission.screenshot_data && (
                  <div>
                    <p className="text-sm text-[#A1A1AA] mb-2 flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Attached Screenshot:
                    </p>
                    <img 
                      src={selectedSubmission.screenshot_data}
                      alt="Screenshot"
                      className="max-w-full rounded-lg border border-white/10"
                    />
                  </div>
                )}

                {/* Status & Notes */}
                <div className="space-y-4 pt-4 border-t border-white/10">
                  <div>
                    <p className="text-sm text-[#A1A1AA] mb-2">Status:</p>
                    <Select 
                      value={selectedSubmission.status} 
                      onValueChange={updateStatus}
                      disabled={updatingStatus}
                    >
                      <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <p className="text-sm text-[#A1A1AA] mb-2">Admin Notes:</p>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add internal notes about this submission..."
                      className="bg-white/5 border-white/10 text-white placeholder:text-[#52525B]"
                    />
                  </div>
                </div>

                {/* Metadata */}
                <div className="flex gap-6 text-xs text-[#52525B]">
                  <p>Created: {formatDate(selectedSubmission.created_at)}</p>
                  <p>Updated: {formatDate(selectedSubmission.updated_at)}</p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setShowDetailModal(false)}
                  className="text-[#A1A1AA] hover:text-white"
                >
                  Close
                </Button>
                <Button
                  onClick={() => updateStatus(selectedSubmission.status)}
                  disabled={updatingStatus}
                  className="bg-[#FF2E63] hover:bg-[#E01E50]"
                >
                  {updatingStatus ? 'Saving...' : 'Save Notes'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminContact;
